import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  WttpQuestionnairesDocument,
  WttpQuestionnaireStatus,
  WttpQuestionnaire,
  Actions,
  Subjects,
  AppArea,
} from 'src/graphql/generated/graphql';
import { WttpQuestionnairesTable } from './table/table';
import styles from './index.module.css';
import { useTable } from 'src/hooks/use-table';
import {
  Accordion,
  Pagination,
  PaginationCounter,
  Tabs,
  Typography,
} from '@otp/ui';
import { CheckedList } from '@otp/icons';
import { useAuthContext } from 'src/context/auth.context';
import { ContentWrapper } from 'src/components/common/wrapper/content/wrapper';
import { TableWrapper } from 'src/components/common/wrapper/table/wrapper';
import { ActionsColumn } from './table/columns/actions';
import { EntityAction } from './table/columns/actions/actions-modals';
import { FilterWrapper } from 'src/components/common/wrapper/filter/wrapper';
import { QuestionnaireFilter, WttpQuestionnaireListFilter } from './filter';
import { useQuestionnaires } from 'src/hooks/query/use-questionnaires';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { CreateQuestionnaireButton } from './create-questionnaire-button';
import { Can } from 'src/utils/can';

const PAGINATION_LIMIT = 10;

enum TabType {
  Custom = 'custom',
  Global = 'global',
}

export const WttpQuestionnaireList = () => {
  const { t } = useTranslation();
  const { isAdmin, user } = useAuthContext();
  const [activeTab, setActiveTab] = useState(
    isAdmin ? TabType.Global : TabType.Custom,
  );
  const {
    page,
    setPage,
    filter: filterData,
    onFilterChange,
    defaultFilter,
  } = useTable<WttpQuestionnaireListFilter, Record<string, any>>();
  const { status: filterStatus, ...filter } = filterData || {};
  const isArchivedFilter = filterStatus === 'archived';
  const [totalCount, setTotalCount] = useState(0);
  const { loading, data } = useQuestionnaires({
    filter: {
      ...filter,
      organizationId:
        activeTab === TabType.Custom ? user?.organizationId : null,
      statuses: isArchivedFilter
        ? undefined
        : filterStatus
        ? ([filterStatus] as WttpQuestionnaireStatus[])
        : [WttpQuestionnaireStatus.Active, WttpQuestionnaireStatus.Draft],
      isActive: !isArchivedFilter,
    },
    page,
    paginationLimit: PAGINATION_LIMIT,
  });
  useEffect(() => {
    setTotalCount(data?.pagination?.totalCount || 0);
  }, [data]);
  const TABS = [
    { label: t('common.custom'), value: TabType.Custom },
    { label: t('common.global'), value: TabType.Global },
  ];

  const wttpQuestionnaires = data?.records || [];

  const onActionFinished = (action: EntityAction) => {
    if (!isAdmin && action === EntityAction.Clone) {
      setActiveTab(TabType.Custom);
    }
  };

  const isInactive = (status?: WttpQuestionnaireStatus | undefined | null) =>
    status === WttpQuestionnaireStatus.Draft;

  const questionaireTitle = (
    entity: WttpQuestionnaire,
    t: TFunction<'translation', undefined>,
  ) => (
    <div className="flex items-center w-full mr-2">
      <CheckedList className="mr-2" />
      <Typography
        fontWeight="bold"
        variant="subtitle1"
        color="var(--mui-palette-primary-800)"
      >
        {entity.name}
      </Typography>
      <div className="ml-auto"></div>
      {isInactive(entity.status) && (
        <div className="mr-4">
          <Typography
            fontWeight="bold"
            variant="subtitle1"
            color="var(--mui-palette-error-800)"
          >
            {t('wttp.questionnaire.list.inactive')}
          </Typography>
        </div>
      )}

      <div className="flex space-x-1">
        <ActionsColumn entity={entity} onActionFinished={onActionFinished} />
      </div>
    </div>
  );

  const onTabChange = (_: any, value: any) => {
    setActiveTab(value);
    setPage(0);
  };

  const showPagination = totalCount > PAGINATION_LIMIT;
  const onPageChange = (page: number) => {
    setPage(page);
  };

  const getPagination = () => (
    <div className={styles.pagination}>
      <Pagination
        page={page}
        count={totalCount}
        rowsPerPage={PAGINATION_LIMIT}
        onChange={onPageChange}
      />
    </div>
  );

  const getPaginationCounter = () => (
    <PaginationCounter
      count={totalCount}
      rowsPerPage={PAGINATION_LIMIT}
      page={page}
    />
  );

  return (
    <>
      <Can I={Actions.Read} a={Subjects.Questionnaires} appArea={AppArea.Wttp}>
        <FilterWrapper>
          <QuestionnaireFilter
            onSubmit={onFilterChange}
            defaultFilter={defaultFilter}
          />
        </FilterWrapper>
        <ContentWrapper>
          <div className={styles.tableWrapperWithOffset}>
            <TableWrapper>
              <div
                className={clsx('relative', { 'flex justify-end': isAdmin })}
              >
                {!isAdmin && (
                  <Tabs tabs={TABS} onChange={onTabChange} value={activeTab} />
                )}
                <CreateQuestionnaireButton
                  refetchQueries={[WttpQuestionnairesDocument]}
                />
              </div>

              {!wttpQuestionnaires?.length && (
                <Typography align="center" className="mt-16">
                  {t('wttp.questionnaire.list.no-data')}
                </Typography>
              )}
              {showPagination && (
                <div className={clsx(styles.titleWrapper, 'mt-4')}>
                  {getPaginationCounter()}
                  {getPagination()}
                </div>
              )}

              {wttpQuestionnaires?.length > 0 && (
                <div className="mt-4">
                  {wttpQuestionnaires.map((questionnaire) => (
                    <Accordion
                      key={questionnaire.id}
                      className={clsx(
                        isInactive(questionnaire.status)
                          ? styles.questionnaireInactive
                          : styles.questionnaireActive,
                        'mb-2 md:px-2 px-1',
                      )}
                      title={questionaireTitle(
                        questionnaire as WttpQuestionnaire,
                        t,
                      )}
                    >
                      <WttpQuestionnairesTable
                        data={questionnaire.questions || []}
                        loading={loading}
                        options={{
                          count: questionnaire.questions?.length,
                          onChangePage: setPage,
                        }}
                      />
                    </Accordion>
                  ))}
                </div>
              )}
              {showPagination && (
                <div className={clsx(styles.paginationWrapper, 'mt-4')}>
                  {getPagination()}
                </div>
              )}
            </TableWrapper>
          </div>
        </ContentWrapper>
      </Can>
    </>
  );
};
